import { Link } from 'react-router-dom';

const UNIFIED = {
  id: 'unified',
  title: 'The Unified Experience',
  subtitle: 'Concierge + Studio + Canvas',
  desc: 'Three modes woven into one adaptive interface. Concierge (chat-first) for creating new sites, Studio (split panel) for active editing, Canvas (visual-first) for day-to-day management. The UI transforms based on what you\'re doing.',
  color: 'from-orange-500/20 via-purple-500/20 to-cyan-500/20',
  icon: 'auto_awesome',
  traits: ['Adaptive 3-mode UI', 'Concierge onboarding', 'Studio editing', 'Canvas overview', 'Shared chat history', 'Automatic backups'],
};

const MOCKUPS = [
  {
    id: 'concierge',
    title: 'The Concierge',
    subtitle: 'Chat-First',
    desc: 'The AI leads everything through conversation. Sites in a left rail, chat in the center, preview slides in when the site is ready. Best for users who don\'t know where to start.',
    color: 'from-orange-500/20 to-amber-500/20',
    icon: 'chat',
    traits: ['AI-led onboarding', 'Conversational UX', 'Progressive disclosure', 'Preview panel slides in'],
  },
  {
    id: 'studio',
    title: 'The Studio',
    subtitle: 'Split Panel',
    desc: 'Side-by-side AI chat and live preview. Tool tabs for content, design, and settings. Version history in the top bar. Feels like a creative workspace.',
    color: 'from-purple-500/20 to-blue-500/20',
    icon: 'view_sidebar',
    traits: ['Chat + preview side-by-side', 'Tool tabs (Content, Design, Settings)', 'Viewport switcher', 'Version dropdown'],
  },
  {
    id: 'canvas',
    title: 'The Canvas',
    subtitle: 'Visual-First',
    desc: 'Your site preview takes center stage, big and bold. Action cards below for quick tasks. AI chat is a floating drawer. Focus is on the visual output.',
    color: 'from-emerald-500/20 to-cyan-500/20',
    icon: 'crop_free',
    traits: ['Large site preview hero', 'Action cards grid', 'Floating chat drawer', 'AI suggestion strip'],
  },
  {
    id: 'flow',
    title: 'The Flow',
    subtitle: 'Guided Wizard',
    desc: 'Step-by-step onboarding: goal, description, branding, build. Transforms into a minimal dashboard after. Zero confusion for first-time users.',
    color: 'from-pink-500/20 to-red-500/20',
    icon: 'timeline',
    traits: ['4-step wizard', 'New / Migrate / Facelift paths', 'Brand upload or AI generate', 'Transforms into dashboard'],
  },
  {
    id: 'thread',
    title: 'The Thread',
    subtitle: 'Mobile Messaging',
    desc: 'Messaging-app feel. Sites are conversations in a list. Tap to chat with your site\'s AI. Device-aware hints. Built for mobile-first users who manage on the go.',
    color: 'from-sky-500/20 to-indigo-500/20',
    icon: 'forum',
    traits: ['Messaging-app UI', 'Sites as threads', 'Inline preview cards', 'Device-aware tips', 'Bottom tab bar'],
  },
];

export default function MockupGallery() {
  return (
    <div className="min-h-screen bg-base">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12">
        {/* Header */}
        <div className="mb-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-400 transition mb-4"
          >
            <span className="material-icons text-sm">arrow_back</span>
            Back to site
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Standard View{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
              Mockups
            </span>
          </h1>
          <p className="text-zinc-400 max-w-2xl">
            Five approaches to a simplified dashboard for users who found WiserWeb searching for
            &quot;AI website builder&quot; or &quot;hosting with AI support&quot;. Each mockup is interactive — click through to try the experience.
          </p>
        </div>

        {/* Unified — featured card */}
        <Link
          to="/mockups/unified"
          className="group block bg-zinc-900/60 border-2 border-orange-500/30 rounded-2xl overflow-hidden hover:border-orange-500/60 transition-all duration-300 mb-8 shadow-glow"
        >
          <div className="flex flex-col md:flex-row">
            <div className={`md:w-56 shrink-0 bg-gradient-to-br ${UNIFIED.color} flex items-center justify-center py-10 md:py-0`}>
              <div className="text-center">
                <span className="material-icons text-5xl text-white/40 group-hover:text-white/60 transition">
                  {UNIFIED.icon}
                </span>
                <p className="text-xs font-bold text-orange-400/80 mt-2 uppercase tracking-wider">Recommended</p>
              </div>
            </div>
            <div className="flex-1 p-5 md:p-6">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-lg font-semibold text-white group-hover:text-orange-400 transition">
                      {UNIFIED.title}
                    </h2>
                    <span className="px-1.5 py-0.5 bg-orange-500 text-white text-[9px] font-bold rounded uppercase">New</span>
                  </div>
                  <span className="text-xs text-orange-400/60 font-medium uppercase tracking-wide">
                    {UNIFIED.subtitle}
                  </span>
                </div>
                <span className="material-icons text-zinc-600 group-hover:text-orange-400 transition mt-1">arrow_forward</span>
              </div>
              <p className="text-sm text-zinc-400 mb-3">{UNIFIED.desc}</p>
              <div className="flex flex-wrap gap-1.5">
                {UNIFIED.traits.map((trait) => (
                  <span key={trait} className="px-2 py-0.5 bg-orange-500/10 border border-orange-500/20 rounded text-[10px] text-orange-400/80">
                    {trait}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Link>

        <h3 className="text-xs font-medium text-zinc-600 uppercase tracking-wider mb-3">Individual concepts</h3>

        {/* Mockup cards */}
        <div className="space-y-4">
          {MOCKUPS.map((mockup, i) => (
            <Link
              key={mockup.id}
              to={`/mockups/${mockup.id}`}
              className="group block bg-zinc-900/60 border border-zinc-800 rounded-2xl overflow-hidden hover:border-orange-500/40 transition-all duration-300"
            >
              <div className="flex flex-col md:flex-row">
                {/* Visual */}
                <div
                  className={`md:w-48 shrink-0 bg-gradient-to-br ${mockup.color} flex items-center justify-center py-8 md:py-0`}
                >
                  <div className="text-center">
                    <span className="material-icons text-4xl text-white/30 group-hover:text-white/50 transition">
                      {mockup.icon}
                    </span>
                    <p className="text-sm font-bold text-white/50 mt-2">{i + 1}</p>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 p-5 md:p-6">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <h2 className="text-lg font-semibold text-white group-hover:text-orange-400 transition">
                        {mockup.title}
                      </h2>
                      <span className="text-xs text-zinc-500 font-medium uppercase tracking-wide">
                        {mockup.subtitle}
                      </span>
                    </div>
                    <span className="material-icons text-zinc-600 group-hover:text-orange-400 transition mt-1">
                      arrow_forward
                    </span>
                  </div>

                  <p className="text-sm text-zinc-400 mb-3">{mockup.desc}</p>

                  <div className="flex flex-wrap gap-1.5">
                    {mockup.traits.map((trait) => (
                      <span
                        key={trait}
                        className="px-2 py-0.5 bg-zinc-800/60 border border-zinc-700/50 rounded text-[10px] text-zinc-500"
                      >
                        {trait}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Context note */}
        <div className="mt-10 p-5 bg-zinc-900/40 border border-zinc-800/50 rounded-xl">
          <h3 className="text-sm font-medium text-white mb-2">Design Philosophy</h3>
          <ul className="space-y-1.5 text-sm text-zinc-500">
            <li className="flex items-start gap-2">
              <span className="material-icons text-orange-400 text-sm mt-0.5">check</span>
              All mockups replace the 15+ item sidebar with a focused, task-driven interface
            </li>
            <li className="flex items-start gap-2">
              <span className="material-icons text-orange-400 text-sm mt-0.5">check</span>
              AI assistant is central — users describe what they want, not how to do it
            </li>
            <li className="flex items-start gap-2">
              <span className="material-icons text-orange-400 text-sm mt-0.5">check</span>
              Automatic backups on every change — version history is always visible
            </li>
            <li className="flex items-start gap-2">
              <span className="material-icons text-orange-400 text-sm mt-0.5">check</span>
              Device-aware: content editing on mobile, layout work on desktop
            </li>
            <li className="flex items-start gap-2">
              <span className="material-icons text-orange-400 text-sm mt-0.5">check</span>
              &quot;Switch to Advanced View&quot; escape hatch for power users
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
